import React, { useEffect, useRef } from "react";
import dompurify from "dompurify";
import { EmailAnalysisParticipant } from "@types";
import { getClassName } from "@utils";
import { EnvelopeIcon } from "./icons";
import { Avatar, AvatarSize, AvatarVariant } from "../Avatar";
import { Skeleton, SkeletonSize } from "../Skeleton";
import css from "./MessageAnalysis.module.scss";

export interface MessageAnalysisProps {
  currentIndex?: number;
  isLoading?: boolean;
  sender?: EmailAnalysisParticipant | null;
  text?: string;
  totalCount?: number;
}

const MessageAnalysis: React.FC<MessageAnalysisProps> = ({
  currentIndex = 0,
  isLoading = false,
  sender,
  text,
  totalCount = 1,
}) => {
  const textRef = useRef<HTMLDivElement>(null);

  const [senderFirstName, senderLastName] = sender?.metadata?.name?.split(" ") || [];
  const senderName = senderLastName ? `${senderFirstName} ${senderLastName[0]}.` : sender?.email;

  const attrs = {
    container: {
      className: css.container,
    },
    header: {
      className: css.header,
    },
    avatarWrapper: {
      className: css.avatarWrapper,
    },
    emailIconAvatar: {
      isLoading,
      placeholder: EnvelopeIcon,
      size: AvatarSize.S,
      variant: sender?.isUser ? AvatarVariant.User : AvatarVariant.Prospect,
    },
    avatar: {
      isLoading,
      size: AvatarSize.S,
      variant: sender?.isUser ? AvatarVariant.User : AvatarVariant.Prospect,
      src: sender?.metadata?.avatarSrc || "",
      name: sender?.metadata?.name || sender?.email || "",
    },
    contactInfoWrapper: {
      className: css.contactInfoWrapper,
    },
    name: {
      className: css.name,
    },
    email: {
      className: css.email,
    },
    counter: {
      className: getClassName(css.counter, isLoading && css.counterLoading),
    },
    textWrapper: {
      className: css.textWrapper,
    },
    text: {
      ref: textRef,
      className: css.text,
    },
    nameSkeleton: {
      className: css.nameSkeleton,
      width: 130,
    },
    emailSkeleton: {
      size: SkeletonSize.S,
      width: 130,
    },
    textSkeleton: {
      className: css.textSkeleton,
      rows: 10,
    },
    counterSkeleton: {
      className: css.textSkeleton,
      width: 12,
    },
  };

  useEffect(() => {
    if (isLoading) {
      textRef.current.innerHTML = "";
      return;
    }

    if (text && textRef.current) {
      try {
        const parser = new DOMParser();
        const parsedContent = parser.parseFromString(text, "text/html");

        // Remove pixels
        parsedContent.body.querySelectorAll('img[width="1"][height="1"]').forEach((node) => {
          node.remove();
        });

        parsedContent.body.querySelectorAll("a").forEach((link) => {
          link.setAttribute("target", "_blank");
          link.setAttribute("rel", "noopener noreferrer");
        });

        parsedContent.body.querySelectorAll("[style]").forEach((node) => {
          if (node instanceof HTMLElement) {
            node.style.removeProperty("color");
            node.style.removeProperty("background-color");
            node.style.removeProperty("font-size");
            node.style.removeProperty("font-family");
            node.style.removeProperty("line-height");
          }
        });

        parsedContent.body.querySelectorAll("font").forEach((node) => {
          node.removeAttribute("color");
          node.removeAttribute("face");
          node.removeAttribute("size");
        });

        const sanitizedContent = dompurify.sanitize(parsedContent.body, {
          USE_PROFILES: { html: true },
          ADD_ATTR: ["target"],
        });

        textRef.current.innerHTML = sanitizedContent;
      } catch (error) {
        console.error(error);
      }
    }
  }, [text, isLoading]);

  return (
    <div {...attrs.container}>
      <div {...attrs.header}>
        <div {...attrs.avatarWrapper}>
          <Avatar {...attrs.emailIconAvatar} />
          <Avatar {...attrs.avatar} />
        </div>
        <div {...attrs.contactInfoWrapper}>
          {isLoading ? (
            <Skeleton {...attrs.nameSkeleton} />
          ) : (
            <span {...attrs.name}>{senderName}</span>
          )}
          {isLoading ? (
            <Skeleton {...attrs.emailSkeleton} />
          ) : (
            <span {...attrs.email}>{sender?.email}</span>
          )}
        </div>
        <div {...attrs.counter}>
          {isLoading ? <Skeleton {...attrs.counterSkeleton} /> : <span>{currentIndex + 1}</span>}
          <span>/</span>
          {isLoading ? <Skeleton {...attrs.counterSkeleton} /> : <span>{totalCount}</span>}
        </div>
      </div>
      <div {...attrs.textWrapper}>
        <div {...attrs.text} />
        {isLoading ? <Skeleton {...attrs.textSkeleton} /> : null}
      </div>
    </div>
  );
};

export default MessageAnalysis;
